import { useCallback, useEffect, useRef, useState } from "react";
import {
  allowanceFor,
  connectionCommand,
  connectionUrl,
  formatDbBytes,
  formatRelativeTime,
  isValidDbName,
  seatsFor,
  DB_ENGINES,
  DB_ENGINE_IDS,
} from "@saucer/core";
import type {
  DatabaseListResponse,
  DbCredentials,
  DbEngine,
  DbTier,
  ManagedDatabase,
  Plan,
} from "@saucer/core";
import { createDatabase, deleteDatabase, listDatabases, rotateDatabasePassword } from "../api.js";
import { navigate } from "../router.js";
import type { Session } from "../app.js";

/**
 * Managed Postgres and ClickHouse.
 *
 * Two views over one payload. `GET /api/databases` already returns every
 * instance the account has, so the detail page reads its instance out of that
 * list rather than adding an endpoint — one D1 round trip covers the index and
 * the detail, and moving between them costs nothing.
 *
 * The one thing this page has to get right is the password. It comes back
 * exactly once, from the call that created or rotated it, and no endpoint will
 * ever return it again — so a credential that has just arrived is held in
 * component state and shown until it is dismissed, and the dismiss button says
 * plainly that dismissing it is the end of it.
 */

/* -- shared state ---------------------------------------------------------- */

function useDatabases() {
  const [state, setState] = useState<DatabaseListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setState(await listDatabases());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Something went wrong.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { state, error, setError, refresh };
}

/* -- the shell ------------------------------------------------------------- */

/**
 * A rail rather than more items in the top bar.
 *
 * An account can hold several instances across two engines, and each one has a
 * page. That is a list that grows, which is the thing a navbar is worst at —
 * so the instances live down the side where there is room for their state, and
 * the top bar keeps carrying the app's sections.
 */
function Shell({
  state,
  active,
  children,
}: {
  state: DatabaseListResponse | null;
  active: string | null;
  children: React.ReactNode;
}) {
  return (
    <main className="dbx">
      <aside className="dbx__rail">
        <a
          className={`dbx__rail-home${active === null ? " dbx__rail-home--on" : ""}`}
          href="/databases"
          onClick={(event) => {
            event.preventDefault();
            navigate("/databases");
          }}
        >
          All databases
        </a>

        {DB_ENGINE_IDS.map((engine) => {
          const mine = state?.databases.filter((database) => database.engine === engine) ?? [];
          if (mine.length === 0) return null;
          return (
            <div key={engine} className="dbx__rail-group">
              <span className="dbx__rail-label">{DB_ENGINES[engine].label}</span>
              {mine.map((database) => (
                <a
                  key={database.id}
                  className={`dbx__rail-item${active === database.id ? " dbx__rail-item--on" : ""}`}
                  href={`/databases/${database.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(`/databases/${database.id}`);
                  }}
                >
                  <span className="dbx__rail-name">{database.name}</span>
                  <StateDot state={database.state} />
                </a>
              ))}
            </div>
          );
        })}

        {state && state.databases.length === 0 ? (
          <p className="dbx__rail-empty meta">Nothing yet.</p>
        ) : null}
      </aside>

      <div className="dbx__main">{children}</div>
    </main>
  );
}

function StateDot({ state }: { state: ManagedDatabase["state"] }) {
  return <i className={`dbx__dot dbx__dot--${state}`} aria-label={state} title={state} />;
}

/* -- index ----------------------------------------------------------------- */

export function Databases({ session }: { session: NonNullable<Session> }) {
  const { state, error, setError, refresh } = useDatabases();
  const [fresh, setFresh] = useState<DbCredentials | null>(null);

  return (
    <Shell state={state} active={null}>
      <header className="dbx__head">
        <h1>Databases</h1>
        <p className="meta">
          Postgres and ClickHouse on our own metal. Clients connect straight to the box, so a
          query costs the network and the query — nothing about it passes through the edge.
        </p>
      </header>

      {error ? (
        <div className="notice notice--error">
          <p>{error}</p>
        </div>
      ) : null}

      {fresh ? <Credentials credentials={fresh} onDismiss={() => setFresh(null)} /> : null}

      {state && !state.available ? (
        <p className="meta">
          Provisioning is not switched on for this deployment yet. Existing databases keep
          working.
        </p>
      ) : null}

      {DB_ENGINE_IDS.map((engine) => (
        <EngineSection
          key={engine}
          engine={engine}
          plan={session.user.plan}
          state={state}
          onCredentials={setFresh}
          onChanged={refresh}
          onError={setError}
        />
      ))}
    </Shell>
  );
}

function EngineSection({
  engine,
  plan,
  state,
  onCredentials,
  onChanged,
  onError,
}: {
  engine: DbEngine;
  plan: Plan;
  state: DatabaseListResponse | null;
  onCredentials: (credentials: DbCredentials) => void;
  onChanged: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const spec = DB_ENGINES[engine];
  const mine = state?.databases.filter((database) => database.engine === engine) ?? [];
  const remaining = state?.remaining[engine] ?? { shared: 0, dedicated: 0 };

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>{spec.label}</h2>
        <span className="meta">{spec.tagline}</span>
      </div>

      {state === null ? (
        <p className="meta">Loading…</p>
      ) : mine.length === 0 ? (
        <p className="meta">Nothing here yet.</p>
      ) : (
        <ul className="dbx__cards">
          {mine.map((database) => (
            <li key={database.id}>
              <a
                className="dbx__card"
                href={`/databases/${database.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/databases/${database.id}`);
                }}
              >
                <span className="dbx__card-top">
                  <strong className="dbx__card-name">{database.name}</strong>
                  <StateBadge state={database.state} />
                </span>
                <span className="dbx__card-host mono">
                  {database.host}:{database.port}
                </span>
                <span className="dbx__card-foot meta">
                  {database.tier} · {formatDbBytes(database.sizeBytes)} of{" "}
                  {formatDbBytes(database.storageBytes)}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {state !== null && state.available ? (
        <CreateForm
          engine={engine}
          remaining={remaining}
          plan={plan}
          onCredentials={onCredentials}
          onChanged={onChanged}
          onError={onError}
        />
      ) : null}
    </section>
  );
}

/* -- detail ---------------------------------------------------------------- */

/**
 * Everything the control plane knows about one instance, on one page.
 *
 * `sizeBytes` is the last sweep's measurement rather than a live figure — the
 * daemon measures on a timer and pushes, so nothing on this page asks the box a
 * question. That is what keeps it a single D1 read, and why the number carries
 * the time it was taken.
 */
export function DatabaseDetail({
  session,
  databaseId,
}: {
  session: NonNullable<Session>;
  databaseId: string;
}) {
  const { state, error, setError, refresh } = useDatabases();
  const [fresh, setFresh] = useState<DbCredentials | null>(null);
  const [busy, setBusy] = useState(false);

  const database = state?.databases.find((candidate) => candidate.id === databaseId) ?? null;

  if (state && !database) {
    return (
      <Shell state={state} active={null}>
        <header className="dbx__head">
          <h1>Not found</h1>
          <p className="meta">That database does not exist, or it belongs to another account.</p>
        </header>
      </Shell>
    );
  }

  const spec = database ? DB_ENGINES[database.engine] : null;
  const allowance = database ? allowanceFor(session.user.plan, database.engine) : null;
  const used = database && database.storageBytes > 0
    ? Math.min(100, (database.sizeBytes / database.storageBytes) * 100)
    : 0;

  return (
    <Shell state={state} active={databaseId}>
      {error ? (
        <div className="notice notice--error">
          <p>{error}</p>
        </div>
      ) : null}

      {fresh ? <Credentials credentials={fresh} onDismiss={() => setFresh(null)} /> : null}

      {!database || !spec || !allowance ? (
        <p className="meta">Loading…</p>
      ) : (
        <>
          <header className="dbx__head">
            <div className="dbx__title">
              <h1>{database.name}</h1>
              <StateBadge state={database.state} />
            </div>
            <p className="meta">
              {spec.label} · {database.tier} · created {formatRelativeTime(database.createdAt)}
            </p>
          </header>

          {database.state === "failed" ? (
            <Failed
              database={database}
              busy={busy}
              setBusy={setBusy}
              onChanged={refresh}
              onCredentials={setFresh}
              onError={setError}
            />
          ) : null}

          {database.state === "ready" ? (
            <>
              <section className="dbx__panel">
                <h2 className="dbx__panel-title">Connect</h2>
                <p className="meta">
                  The password is not stored anywhere it can be read back, so it is not shown
                  here. Rotate below for a new one.
                </p>
                <dl className="dbx__facts">
                  <Fact label="Host" value={database.host} mono copy />
                  <Fact label="Port" value={String(database.port)} mono />
                  <Fact label="Database" value={database.database} mono copy />
                  <Fact label="User" value={database.login} mono copy />
                </dl>
                <Copyable
                  label="Connection URL"
                  value={connectionUrl(database, "<password>")}
                />
                <Copyable
                  label="Command line"
                  value={connectionCommand(database, "<password>")}
                />
              </section>

              <section className="dbx__panel">
                <h2 className="dbx__panel-title">Storage</h2>
                <div className="dbx__meter">
                  <span className="dbx__meter-fill" style={{ inlineSize: `${used}%` }} />
                </div>
                <p className="meta">
                  {formatDbBytes(database.sizeBytes)} of {formatDbBytes(database.storageBytes)}{" "}
                  used
                  {database.usageAt
                    ? ` · measured ${formatRelativeTime(database.usageAt)}`
                    : " · not measured yet"}
                </p>
                <p className="meta">
                  Going over refuses new writes and never touches reads: endpoints and queries
                  keep answering while ingest stops.
                </p>
              </section>

              <section className="dbx__panel">
                <h2 className="dbx__panel-title">Limits</h2>
                <dl className="dbx__facts">
                  <Fact label="Connections" value={String(database.maxConnections)} />
                  <Fact label="Storage" value={formatDbBytes(database.storageBytes)} />
                  <Fact label="Tier" value={database.tier} />
                  <Fact label="Plan" value={session.user.plan} />
                </dl>
              </section>

              {database.engine === "clickhouse" ? (
                <section className="dbx__panel">
                  <h2 className="dbx__panel-title">Analytics</h2>
                  <p className="meta">
                    Land events, shape them with pipes, and publish a pipe as a parameterised
                    HTTP endpoint answered beside the data.
                  </p>
                  <span>
                    <button
                      className="button"
                      type="button"
                      onClick={() => navigate(`/workspaces/${database.id}`)}
                    >
                      Open workspace
                    </button>
                  </span>
                </section>
              ) : null}
            </>
          ) : null}

          <section className="dbx__panel">
            <h2 className="dbx__panel-title">Actions</h2>
            <div className="row">
              {database.state === "ready" ? (
                <button
                  className="button"
                  type="button"
                  disabled={busy}
                  title="Issue a new password. The current one stops working immediately."
                  onClick={async () => {
                    setBusy(true);
                    try {
                      setFresh(await rotateDatabasePassword(database.id));
                    } catch (cause) {
                      setError(
                        cause instanceof Error ? cause.message : "Could not rotate that password.",
                      );
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Rotate password
                </button>
              ) : null}
              <button
                className="button button--danger"
                type="button"
                disabled={busy}
                onClick={async () => {
                  if (!window.confirm(`Delete ${database.name} and everything in it?`)) return;
                  setBusy(true);
                  try {
                    await deleteDatabase(database.id);
                    navigate("/databases");
                  } catch (cause) {
                    setError(cause instanceof Error ? cause.message : "Could not delete that.");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Delete
              </button>
            </div>
            <p className="meta">
              Deleting takes the data with it and there is no snapshot to go back to.
            </p>
          </section>
        </>
      )}
    </Shell>
  );
}

/**
 * A row whose provisioning did not finish.
 *
 * It holds no seat — the seat count ignores `failed` — so the only thing it
 * costs is the name. Retry is delete-then-create with the same name rather
 * than a repair: nothing was successfully built, so there is nothing to mend.
 */
function Failed({
  database,
  busy,
  setBusy,
  onChanged,
  onCredentials,
  onError,
}: {
  database: ManagedDatabase;
  busy: boolean;
  setBusy: (value: boolean) => void;
  onChanged: () => Promise<void>;
  onCredentials: (credentials: DbCredentials) => void;
  onError: (message: string) => void;
}) {
  return (
    <div className="notice notice--error stack stack--tight">
      <div className="section-head">
        <h2>Provisioning failed</h2>
        <span className="meta">
          Nothing was built, and this holds no slot on your plan — only the name.
        </span>
      </div>
      {database.error ? <code className="token mono">{database.error}</code> : null}
      <div className="row">
        <button
          className="button"
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await deleteDatabase(database.id);
              onCredentials(
                await createDatabase({
                  engine: database.engine,
                  tier: database.tier,
                  name: database.name,
                }),
              );
              await onChanged();
              navigate("/databases");
            } catch (cause) {
              onError(cause instanceof Error ? cause.message : "Could not retry that.");
              await onChanged();
            } finally {
              setBusy(false);
            }
          }}
        >
          Retry
        </button>
        <button
          className="button button--quiet"
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await deleteDatabase(database.id);
              navigate("/databases");
            } catch (cause) {
              onError(cause instanceof Error ? cause.message : "Could not delete that.");
            } finally {
              setBusy(false);
            }
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}

function Fact({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div className="dbx__fact">
      <dt className="meta">{label}</dt>
      <dd className={mono ? "mono" : undefined}>
        {value}
        {copy ? <CopyMark value={value} /> : null}
      </dd>
    </div>
  );
}

function CopyMark({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="dbx__copy"
      type="button"
      aria-label={`Copy ${value}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard access can be refused; the text is selectable anyway.
        }
      }}
    >
      {copied ? "copied" : "copy"}
    </button>
  );
}

function StateBadge({ state }: { state: ManagedDatabase["state"] }) {
  return (
    <span className={`dbx__badge dbx__badge--${state}`}>
      <StateDot state={state} />
      {state}
    </span>
  );
}

/* -- creating -------------------------------------------------------------- */

const STEPS = [
  { key: "claim", label: "Reserving the name" },
  { key: "build", label: "Building on the box" },
  { key: "done", label: "Ready" },
] as const;

function CreateForm({
  engine,
  remaining,
  plan,
  onCredentials,
  onChanged,
  onError,
}: {
  engine: DbEngine;
  remaining: Record<DbTier, number>;
  plan: Plan;
  onCredentials: (credentials: DbCredentials) => void;
  onChanged: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState("");
  const [tier, setTier] = useState<DbTier>("shared");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<"claim" | "build" | "done">("claim");
  const [elapsed, setElapsed] = useState(0);

  const named = name.trim();
  const nameOk = named.length > 0 && isValidDbName(named);
  const seatsLeft = remaining[tier];
  // Asked of the same table the Worker enforces from, rather than spelled out
  // again as a plan name. A plan that gets dedicated seats then gets the option
  // here without this file being touched — and, more to the point, cannot be
  // offered the option here while the API refuses it.
  const dedicatedOffered = seatsFor(plan, engine, "dedicated") > 0;

  // A ticking clock while the box works, because the honest thing to show for a
  // step whose duration we do not know is how long it has actually taken.
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!busy) {
      if (timer.current !== null) window.clearInterval(timer.current);
      return;
    }
    timer.current = window.setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
  }, [busy]);

  return (
    <form
      className="dbx__create"
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy || !nameOk || seatsLeft === 0) return;
        setBusy(true);
        setElapsed(0);
        setPhase("claim");

        // The row is written before the daemon is called, so the moment the
        // request is in flight the name is taken — that step is genuinely done.
        window.setTimeout(() => setPhase("build"), 350);

        try {
          const credentials = await createDatabase({ engine, tier, name: named });
          setPhase("done");
          onCredentials(credentials);
          setName("");
          await onChanged();
        } catch (cause) {
          onError(cause instanceof Error ? cause.message : "Could not create that database.");
          // The row may now exist in `failed`, which the list will show.
          await onChanged();
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="row">
        <input
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="name"
          disabled={busy}
          aria-label={`New ${DB_ENGINES[engine].label} database name`}
        />
        <select
          className="input"
          value={tier}
          onChange={(event) => setTier(event.target.value as DbTier)}
          disabled={busy}
          aria-label="Tier"
        >
          <option value="shared">shared</option>
          <option value="dedicated" disabled={!dedicatedOffered}>
            dedicated
          </option>
        </select>
        <button className="button" type="submit" disabled={busy || !nameOk || seatsLeft === 0}>
          {busy ? "Creating…" : "Create"}
        </button>
      </div>

      {busy ? (
        <ol className="dbx__steps">
          {STEPS.map((step) => {
            const order = STEPS.findIndex((candidate) => candidate.key === step.key);
            const at = STEPS.findIndex((candidate) => candidate.key === phase);
            const status = order < at ? "done" : order === at ? "now" : "next";
            return (
              <li key={step.key} className={`dbx__step dbx__step--${status}`}>
                <span className="dbx__step-mark" aria-hidden="true" />
                {step.label}
                {status === "now" && step.key === "build" ? (
                  <span className="meta"> · {elapsed}s</span>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : (
        <span className="meta">{hint(engine, tier, plan, seatsLeft, name, nameOk)}</span>
      )}

      {busy && tier === "dedicated" ? (
        <span className="meta">
          A dedicated instance starts its own container, so the first one on a box can take a
          minute while the image is pulled.
        </span>
      ) : null}
    </form>
  );
}

function hint(
  engine: DbEngine,
  tier: DbTier,
  plan: Plan,
  seatsLeft: number,
  name: string,
  nameOk: boolean,
): string {
  if (name.length > 0 && !nameOk) {
    return "Lowercase letters, digits and dashes, starting with a letter.";
  }
  if (seatsLeft === 0) {
    // No seats at all is a plan that does not include the tier; no seats *left*
    // is one that does and they are all in use. Different sentences, and the
    // difference is the seat table's to know.
    return seatsFor(plan, engine, tier) === 0
      ? `${tier === "dedicated" ? "Dedicated instances are" : "This tier is"} not on your plan.`
      : `No ${tier} ${DB_ENGINES[engine].label} slots left on your plan.`;
  }
  return `${seatsLeft} ${tier} ${seatsLeft === 1 ? "slot" : "slots"} left.`;
}

/**
 * Shown once, and said to be once. Losing this means rotating for a new one,
 * so the button is worded as an acknowledgement rather than as a close box.
 */
function Credentials({
  credentials,
  onDismiss,
}: {
  credentials: DbCredentials;
  onDismiss: () => void;
}) {
  const { database, password } = credentials;

  return (
    <div className="notice stack stack--tight">
      <div className="section-head">
        <h2>{database.name} is ready</h2>
        <span className="meta">
          This password is shown once and is not stored anywhere it can be read back. Copy it
          now — if you lose it, rotate for a new one.
        </span>
      </div>

      <Copyable label="Connection URL" value={connectionUrl(database, password)} />
      <Copyable label="Command line" value={connectionCommand(database, password)} />

      <span>
        <button className="button" type="button" onClick={onDismiss}>
          I have copied it
        </button>
      </span>
    </div>
  );
}

function Copyable({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="field">
      <span className="meta">{label}</span>
      <div className="row">
        <code className="token mono">{value}</code>
        <button
          className="button button--quiet"
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            } catch {
              // Clipboard access can be refused; the text is selectable anyway.
            }
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
